import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../AuthContext";
import {
	GITLAB_BASE_URL,
	GITLAB_GRAPHQL_EXPERIMENTAL_URL,
	GITLAB_GRAPHQL_URL,
	REFRESH_INTERVAL,
} from "../constants";
import { isAi, withAvatarBase } from "../gitlabHtml";
import {
	annotateStackedMergeRequests,
	findMissingStackParents,
} from "../stackedMergeRequests";
import {
	ENRICHMENT_BATCH_SIZE,
	ENRICHMENT_CONCURRENCY,
	createEnrichmentQuery,
} from "./createEnrichmentQuery";
import { isRetryableGraphqlErrors } from "./graphqlErrors";
import { mergePipelines } from "./mergePipelines";
import { projectRules } from "./projectRules";
import { useCachedStorage } from "./useCachedStorage";

const MR_FIELDS_LIGHTWEIGHT = `
  id
  iid
  webPath
  title
  createdAt
  updatedAt
  state
  draft
  reference
  sourceBranch
  sourceBranchProtected
  targetBranch
  conflicts
  userNotesCount
  author {
    avatarUrl
    name
    username
    bot
  }
  project {
    archived
    fullPath
  }
  sourceProject {
    fullPath
  }
  targetProject {
    fullPath
  }
`;

const REVIEW_REQUESTS_QUERY = `
  query getReviewRequests {
    currentUser {
      reviewRequests: reviewRequestedMergeRequests(state: opened, sort: UPDATED_DESC) {
        nodes {
          ${MR_FIELDS_LIGHTWEIGHT}
        }
      }
    }
  }
`;

const MY_OPEN_MRS_QUERY = `
  query getMyOpenMRs {
    currentUser {
      openRequests: authoredMergeRequests(state: opened, sort: UPDATED_DESC) {
        nodes {
          ${MR_FIELDS_LIGHTWEIGHT}
        }
      }
    }
  }
`;

// Separate user profile query that doesn't need frequent updates
const USER_PROFILE_QUERY = `
  query getUserProfile {
    currentUser {
      avatarUrl
      username
    }
  }
`;

const CLOSED_DATA_QUERY = `
  query getClosedData {
    currentUser {
      mergedRequests: authoredMergeRequests(state: merged, sort: MERGED_AT_DESC, first: 20) {
        nodes {
          ${MR_FIELDS_LIGHTWEIGHT}
        }
      }
      closedRequests: authoredMergeRequests(state: closed, sort: CLOSED_AT_DESC, first: 20) {
        nodes {
          ${MR_FIELDS_LIGHTWEIGHT}
        }
      }
      mergedReviewRequests: reviewRequestedMergeRequests(state: merged, sort: MERGED_AT_DESC, first: 20) {
        nodes {
          ${MR_FIELDS_LIGHTWEIGHT}
        }
      }
      closedReviewRequests: reviewRequestedMergeRequests(state: closed, sort: CLOSED_AT_DESC, first: 20) {
        nodes {
          ${MR_FIELDS_LIGHTWEIGHT}
        }
      }
    }
  }
`;

const hasRequiredListData = (query, data) => {
	if (query === REVIEW_REQUESTS_QUERY) {
		return Array.isArray(data?.currentUser?.reviewRequests?.nodes);
	}
	if (query === MY_OPEN_MRS_QUERY) {
		return Array.isArray(data?.currentUser?.openRequests?.nodes);
	}
	if (query === CLOSED_DATA_QUERY) {
		const currentUser = data?.currentUser;
		return (
			Array.isArray(currentUser?.mergedRequests?.nodes) &&
			Array.isArray(currentUser?.closedRequests?.nodes) &&
			Array.isArray(currentUser?.mergedReviewRequests?.nodes) &&
			Array.isArray(currentUser?.closedReviewRequests?.nodes)
		);
	}
	if (query.includes("getWatchedProjectMRs")) {
		return Array.isArray(data?.project?.mergeRequests?.nodes);
	}
	return true;
};
const excludeArchivedProjects = (mr) => !mr.project.archived;
const excludeMyPrs = (mr) => !mr.author.isMe;

const inFlightRequests = new Map();
const PROJECT_ENRICHMENT_CONCURRENCY = 4;
const REQUEST_TIMEOUT_MS = 15000;
const REQUEST_RETRY_DELAYS_MS = [400, 1000];
// Bounds on walking up stacks past the MRs on the dashboard.
const STACK_LOOKUP_ROUNDS = 4;
const STACK_LOOKUP_MAX_MRS = 40;
const STACK_LOOKUP_PROJECTS_PER_QUERY = 10;
// GitLab caps query complexity at 250; each lightweight MR costs about 28.
const MRS_BY_ID_BATCH_SIZE = 8;
// A batch of 20 blocker lookups measured 123 complexity.
const BLOCKERS_BATCH_SIZE = 20;

const createMergeRequestsByIdQuery = (ids) => `
  query getMergeRequestsById {
    ${ids
			.map(
				(id, index) =>
					`mergeRequest${index}: mergeRequest(id: ${JSON.stringify(id)}) { ${MR_FIELDS_LIGHTWEIGHT} }`,
			)
			.join("\n")}
  }
`;

const wait = (milliseconds) =>
	new Promise((resolve) => setTimeout(resolve, milliseconds));

const dedupeRequest = async (key, requestFn) => {
	if (inFlightRequests.has(key)) {
		return inFlightRequests.get(key);
	}

	const promise = requestFn().finally(() => {
		inFlightRequests.delete(key);
	});

	inFlightRequests.set(key, promise);
	return promise;
};

const runWithConcurrency = async (tasks, concurrency) => {
	const results = new Array(tasks.length);
	let nextTask = 0;

	const workers = Array.from(
		{ length: Math.min(concurrency, tasks.length) },
		async () => {
			while (nextTask < tasks.length) {
				const taskIndex = nextTask++;
				results[taskIndex] = await tasks[taskIndex]();
			}
		},
	);

	await Promise.all(workers);
	return results;
};

export const useMergeRequests = (watchProjects) => {
	const { user } = useAuth();
	const [isLoadingOpen, setIsLoadingOpen] = useState(false);
	const [isLoadingClosed, setIsLoadingClosed] = useState(false);
	const [hasQueries, setHasQueries] = useState(false);
	const [error, setError] = useState(null);
	const [lastUpdated, setLastUpdated] = useState(null);
	const [lastFailedRequest, setLastFailedRequest] = useState(null);
	// Lets a slow stack lookup tell that a newer refresh has superseded it.
	const openFetchId = useRef(0);
	// The last refresh's stack lookups, reused until the new ones finish so
	// chains don't break apart while a refresh is in flight.
	const lastStackData = useRef({ blockersById: new Map(), externalMRs: [] });
	const [data, setData] = useCachedStorage("gitlab-mr-data", {
		pendingReviewMRs: [],
		myOpenMRs: [],
		recentlyClosedMRs: [],
		userAvatar: null,
		username: null,
		loaded: false,
		hasIncompleteResults: false,
	});
	const [profileFetched, setProfileFetched] = useState(false);

	const executeQuery = useCallback(
		async (query, variables = {}, { url = GITLAB_GRAPHQL_URL } = {}) => {
			setHasQueries(true);
			const key = JSON.stringify({ query, variables, url });

			return dedupeRequest(key, async () => {
				let lastError;

				for (
					let attempt = 0;
					attempt <= REQUEST_RETRY_DELAYS_MS.length;
					attempt++
				) {
					const controller = new AbortController();
					const timeoutId = setTimeout(
						() => controller.abort(),
						REQUEST_TIMEOUT_MS,
					);

					try {
						const response = await fetch(url, {
							method: "POST",
							headers: {
								"Content-Type": "application/json",
								Authorization: `Bearer ${user?.access_token}`,
							},
							body: JSON.stringify({ query, variables }),
							signal: controller.signal,
						});

						if (!response.ok) {
							const error = new Error(
								`GraphQL request failed (${response.status})`,
							);
							error.retryable =
								response.status === 429 || response.status >= 500;
							throw error;
						}

						const result = await response.json();
						if (result.errors) {
							if (result.data && hasRequiredListData(query, result.data)) {
								console.error("Incomplete GraphQL results:", result.errors);
								setError(
									"Gitlab returned partial data from some queries, some details may not be shown.",
								);
								return { data: result.data, incomplete: true };
							}
							const error = new Error(result.errors[0].message);
							error.retryable = isRetryableGraphqlErrors(result.errors);
							throw error;
						}

						setLastFailedRequest(null);
						return { data: result.data, incomplete: false };
					} catch (error) {
						lastError =
							error.name === "AbortError"
								? new Error("GraphQL request timed out")
								: error;
						const canRetry =
							attempt < REQUEST_RETRY_DELAYS_MS.length &&
							(error.name === "AbortError" ||
								error.retryable ||
								error instanceof TypeError);
						if (!canRetry) break;
						await wait(REQUEST_RETRY_DELAYS_MS[attempt]);
					} finally {
						clearTimeout(timeoutId);
					}
				}

				setLastFailedRequest({
					query,
					variables,
					accessToken: user?.access_token,
				});
				throw lastError;
			}).finally(() => {
				if (inFlightRequests.size === 0) {
					setHasQueries(false);
				}
			});
		},
		[user],
	);

	const transformMergeRequest = useCallback(
		(mr) => {
			if (!mr) {
				console.warn("Received null merge request in transformMergeRequest");
				return null;
			}

			const pipeline = mergePipelines(mr);
			const approvers = mr.approvedBy?.nodes || [];
			const reviewers = mr.reviewers?.nodes || [];
			const aiReviewMissing = !approvers.some(isAi);
			const hasHumanReview =
				approvers.some((reviewer) => !isAi(reviewer)) ||
				reviewers.some((reviewer) => {
					const reviewState = reviewer.mergeRequestInteraction?.reviewState;
					return (
						!isAi(reviewer) &&
						reviewState &&
						!["UNREVIEWED", "REVIEW_STARTED"].includes(reviewState)
					);
				});

			return {
				id: mr.id,
				iid: mr.iid,
				webUrl: `${GITLAB_BASE_URL}${mr.webPath}`,
				title: mr.title,
				createdAt: mr.createdAt,
				updatedAt: mr.updatedAt,
				state: mr.state,
				draft: mr.draft,
				reference: mr.reference,
				sourceBranch: mr.sourceBranch,
				sourceBranchProtected: mr.sourceBranchProtected,
				targetBranch: mr.targetBranch,
				sourceProject: mr.sourceProject,
				targetProject: mr.targetProject,
				projectPath:
					mr.reference?.split("!")[0] ||
					mr.webPath?.split("/-/")[0]?.split("/").slice(-2).join("/") ||
					"",
				userNotesCount: mr.userNotesCount,
				conflicts: mr.conflicts,
				author: {
					name: mr.author.name,
					username: mr.author.username,
					avatarUrl: withAvatarBase(mr.author.avatarUrl),
					bot: mr.author.bot || mr.author.username === "renovate",
					isMe: user?.profile?.username === mr.author.username,
				},
				diffStatsSummary: mr.diffStatsSummary || {
					additions: 0,
					changes: 0,
					deletions: 0,
				},
				discussions: Math.max(
					0,
					(mr.resolvableDiscussionsCount || 0) -
						(mr.resolvedDiscussionsCount || 0),
				),
				aiReviewMissing,
				hasHumanReview,
				headPipeline:
					(pipeline && {
						status: pipeline.active ? "running" : pipeline.status.toLowerCase(),
						latest: pipeline.latest,
						name: pipeline.name,
						totalJobs: pipeline.totalJobs?.count || 0,
						failedJobs: pipeline.failedJobs?.count || 0,
						passedJobs: pipeline.passedJobs?.count || 0,
						mergeRequestEventType: pipeline.mergeRequestEventType,
					}) ||
					null,
				approvalsLeft: mr.approvalsLeft,
				approvers: approvers.map((reviewer) => ({
					avatarUrl: withAvatarBase(reviewer.avatarUrl),
					name: reviewer.name,
					username: reviewer.username,
				})),
				reviewers,
				/* UNREVIEWED, REVIEWED, REQUESTED_CHANGES, APPROVED, UNAPPROVED, REVIEW_STARTED */
				myReview:
					mr.reviewers?.nodes?.find(
						(r) => r.username === user?.profile?.username,
					)?.mergeRequestInteraction?.reviewState ||
					((mr.approvedBy?.nodes || []).find(
						(reviewer) => reviewer.username === user?.profile?.username,
					)
						? "APPROVED"
						: "UNREVIEWED"),
				project: mr.project,
				...(mr.projectFields && { projectFields: mr.projectFields }),
			};
		},
		[user],
	);

	// Enrich MRs with expensive fields in batches to avoid timeouts
	const enrichMergeRequestsWithExpensiveFields = useCallback(
		async (mrs) => {
			if (!mrs?.length) return;

			const tasks = [];
			for (let i = 0; i < mrs.length; i += ENRICHMENT_BATCH_SIZE) {
				const batch = mrs.slice(i, i + ENRICHMENT_BATCH_SIZE);
				tasks.push(async () => {
					const query = createEnrichmentQuery(batch);

					try {
						const result = await executeQuery(query);
						for (const [index, originalMR] of batch.entries()) {
							const enrichedMR = result.data[`mergeRequest${index}`];
							if (enrichedMR) {
								Object.assign(originalMR, {
									diffStatsSummary: enrichedMR.diffStatsSummary,
									pipelines: enrichedMR.pipelines,
									headPipeline: enrichedMR.headPipeline,
									approvedBy: enrichedMR.approvedBy,
									reviewers: enrichedMR.reviewers,
									approvalsLeft: enrichedMR.approvalsLeft,
									resolvableDiscussionsCount:
										enrichedMR.resolvableDiscussionsCount,
									resolvedDiscussionsCount: enrichedMR.resolvedDiscussionsCount,
								});
							}
						}
					} catch (err) {
						const projects = [
							...new Set(batch.map((mr) => mr.project.fullPath)),
						].join(", ");
						console.error(`Error enriching MRs for projects ${projects}:`, err);
					}
				});
			}

			await runWithConcurrency(tasks, ENRICHMENT_CONCURRENCY);
		},
		[executeQuery],
	);

	// Set each MR's open explicit dependencies ("blocked by"). The field is
	// experimental, so it is fetched apart from the enrichment, from the endpoint
	// that still exposes it; a failure only leaves MRs without dependencies.
	const fetchBlockingMergeRequests = useCallback(
		async (mrs) => {
			const tasks = [];
			for (let i = 0; i < mrs.length; i += BLOCKERS_BATCH_SIZE) {
				const batch = mrs.slice(i, i + BLOCKERS_BATCH_SIZE);
				tasks.push(async () => {
					const fields = batch
						.map(
							({ id }, index) => `
								mergeRequest${index}: mergeRequest(id: ${JSON.stringify(id)}) {
									blockingMergeRequests {
										hiddenCount
										visibleMergeRequests { id state }
									}
								}`,
						)
						.join("\n");
					try {
						const { data } = await executeQuery(
							`query getBlockingMRs { ${fields} }`,
							{},
							{ url: GITLAB_GRAPHQL_EXPERIMENTAL_URL },
						);
						for (const [index, mr] of batch.entries()) {
							const blocking =
								data[`mergeRequest${index}`]?.blockingMergeRequests;
							mr.blockingMergeRequests = (blocking?.visibleMergeRequests || [])
								.filter((blocker) => blocker?.state === "opened")
								.map(({ id }) => ({ id }));
							mr.hiddenBlockingCount = blocking?.hiddenCount || 0;
						}
					} catch (err) {
						console.error("Error loading merge request dependencies:", err);
					}
				});
			}

			await runWithConcurrency(tasks, ENRICHMENT_CONCURRENCY);
		},
		[executeQuery],
	);

	// Fetch project-specific fields independently from the generic enrichment.
	const fetchProjectMergeRequestFields = useCallback(
		async (mrs) => {
			// Group MRs by project that need additional fields
			const projectMrs = {};

			for (const mr of mrs) {
				const projectPath = mr.project.fullPath;

				if (projectRules?.[projectPath]?.mergeRequestFields) {
					projectMrs[projectPath] ??= {};
					projectMrs[projectPath][mr.iid] = mr;
				}
			}

			const tasks = [];

			for (const projectFullPath in projectMrs) {
				const projectRule = projectRules[projectFullPath];
				const allIids = Object.keys(projectMrs[projectFullPath]);
				const fieldBatches = [
					{
						fields: projectRule.mergeRequestFields,
						iids: allIids,
					},
				];

				if (projectRule.batchedMergeRequestFields) {
					const { batchSize, fields } = projectRule.batchedMergeRequestFields;
					for (let i = 0; i < allIids.length; i += batchSize) {
						fieldBatches.push({
							fields,
							iids: allIids.slice(i, i + batchSize),
						});
					}
				}

				for (const { fields, iids } of fieldBatches) {
					tasks.push(async () => {
						const iidList = iids.map((iid) => JSON.stringify(iid)).join(",");
						const query = `
	          query getProjectMrFields {
	            project(fullPath: ${JSON.stringify(projectFullPath)}) {
	              mergeRequests(iids: [${iidList}]) {
	                nodes {
	                  iid
	                  ${fields}
	                }
	              }
	            }
	          }`;

						const result = await executeQuery(query);
						const nodes = result.data.project.mergeRequests.nodes;
						for (const node of nodes) {
							const mr = projectMrs[projectFullPath][node.iid];
							mr.projectFields = { ...mr.projectFields, ...node };
						}
					});
				}
			}

			await runWithConcurrency(tasks, PROJECT_ENRICHMENT_CONCURRENCY);
		},
		[executeQuery],
	);

	// Load the MRs that complete the stacks of `openMRs` but are not on the
	// dashboard: explicit dependencies, and the MRs owning the branches that
	// stacked MRs target. Walks up a few levels; failures only shorten chains.
	const fetchExternalStackMembers = useCallback(
		async (openMRs) => {
			const known = [...openMRs];
			const external = [];
			const lookedUpBranches = new Set();

			try {
				for (let round = 0; round < STACK_LOOKUP_ROUNDS; round++) {
					const { blockerIds, branchLookups } = findMissingStackParents(known);
					const ids = new Set(blockerIds);

					const lookups = branchLookups
						.map(({ projectPath, branches }) => ({
							projectPath,
							branches: branches.filter(
								(branch) => !lookedUpBranches.has(`${projectPath}:${branch}`),
							),
						}))
						.filter(({ branches }) => branches.length > 0)
						.slice(0, STACK_LOOKUP_PROJECTS_PER_QUERY);
					if (lookups.length > 0) {
						for (const { projectPath, branches } of lookups) {
							for (const branch of branches) {
								lookedUpBranches.add(`${projectPath}:${branch}`);
							}
						}
						const projects = lookups
							.map(
								({ projectPath, branches }, index) => `
									project${index}: project(fullPath: ${JSON.stringify(projectPath)}) {
										mergeRequests(state: opened, sourceBranches: ${JSON.stringify(branches)}, first: 20) {
											nodes { id }
										}
									}`,
							)
							.join("\n");
						const result = await executeQuery(
							`query findStackParents { ${projects} }`,
						);
						for (const index of lookups.keys()) {
							for (const node of result.data[`project${index}`]?.mergeRequests
								?.nodes || []) {
								if (node?.id) ids.add(node.id);
							}
						}
					}

					const knownIds = new Set(known.map((mr) => mr.id));
					const newIds = [...ids]
						.filter((id) => !knownIds.has(id))
						.slice(0, STACK_LOOKUP_MAX_MRS - external.length);
					if (newIds.length === 0 && lookups.length === 0) break;

					const batches = [];
					for (let i = 0; i < newIds.length; i += MRS_BY_ID_BATCH_SIZE) {
						batches.push(newIds.slice(i, i + MRS_BY_ID_BATCH_SIZE));
					}
					const results = await runWithConcurrency(
						batches.map((batch) => async () => {
							const { data } = await executeQuery(
								createMergeRequestsByIdQuery(batch),
							);
							return batch.map((_, index) => data[`mergeRequest${index}`]);
						}),
						ENRICHMENT_CONCURRENCY,
					);
					const nodes = results.flat().filter((mr) => mr?.state === "opened");
					await enrichMergeRequestsWithExpensiveFields(nodes);
					const found = nodes
						.map(transformMergeRequest)
						.filter((mr) => mr && excludeArchivedProjects(mr))
						.map((mr) => ({ ...mr, external: true }));
					await fetchBlockingMergeRequests(found);

					known.push(...found);
					external.push(...found);
					if (external.length >= STACK_LOOKUP_MAX_MRS) break;
				}
			} catch (err) {
				console.error("Error loading stack members:", err);
			}

			return external;
		},
		[
			executeQuery,
			transformMergeRequest,
			enrichMergeRequestsWithExpensiveFields,
			fetchBlockingMergeRequests,
		],
	);

	const applyProjectRules = useCallback((mrs) => {
		for (const mr of mrs) {
			const projectPath = mr.project.fullPath;
			mr.additionalRules = projectRules?.[projectPath]?.additionalRules?.(mr);
		}
	}, []);

	// Fetch user profile data - only needs to run once
	const fetchUserProfile = useCallback(async () => {
		if (profileFetched || !user?.access_token) return;

		return dedupeRequest("fetchUserProfile", async () => {
			try {
				const profileResult = await executeQuery(USER_PROFILE_QUERY);
				setData((prevData) => ({
					...prevData,
					userAvatar: withAvatarBase(profileResult.data.currentUser.avatarUrl),
					username: profileResult.data.currentUser.username,
				}));
				setProfileFetched(true);
			} catch (err) {
				console.error("Error fetching user profile:", err);
			}
		});
	}, [executeQuery, profileFetched, user, setData]);

	const fetchOpenData = useCallback(async () => {
		if (!user?.access_token) return;

		const fetchId = ++openFetchId.current;
		setIsLoadingOpen(true);
		setError("");
		let hasAnyIncompleteResults = false;

		try {
			// Fetch watched project MRs with lightweight fields
			const watchedProjectQueries = Object.values(watchProjects).map(
				(project) =>
					executeQuery(`
          query getWatchedProjectMRs {
            project (fullPath: "${project.id}") {
              mergeRequests (state: opened, sort: UPDATED_DESC, first: 50) {
                nodes {
                  ${MR_FIELDS_LIGHTWEIGHT}
                }
              }
            }
          }
        `),
			);

			// Execute queries in parallel
			const [
				reviewRequestsResponse,
				watchedProjectResponses,
				myOpenMRsResponse,
			] = await Promise.allSettled([
				executeQuery(REVIEW_REQUESTS_QUERY),
				Promise.allSettled(watchedProjectQueries),
				executeQuery(MY_OPEN_MRS_QUERY),
			]);

			// Track if any query returned incomplete results
			if (
				reviewRequestsResponse.status === "fulfilled" &&
				reviewRequestsResponse.value?.incomplete
			) {
				hasAnyIncompleteResults = true;
			}
			if (watchedProjectResponses.status === "fulfilled") {
				for (const projectResponse of watchedProjectResponses.value) {
					if (
						projectResponse.status === "fulfilled" &&
						projectResponse.value?.incomplete
					) {
						hasAnyIncompleteResults = true;
					}
				}
			}
			if (
				myOpenMRsResponse.status === "fulfilled" &&
				myOpenMRsResponse.value?.incomplete
			) {
				hasAnyIncompleteResults = true;
			}

			// Process review requests
			let reviewRequests = [];

			if (reviewRequestsResponse.status !== "fulfilled") {
				console.error(
					"Error fetching review requests:",
					reviewRequestsResponse.reason,
				);
				throw new Error("Could not retrieve all Merge Requests to review");
			}

			reviewRequests = (
				reviewRequestsResponse.value.data.currentUser.reviewRequests.nodes || []
			).filter((mr) => mr != null);

			// Process watched project MRs
			if (watchedProjectResponses.status !== "fulfilled") {
				console.error(
					"Error fetching any project review requests:",
					watchedProjectResponses.reason,
				);
				throw new Error(
					"Could not retrieve any Project Merge Requests to review",
				);
			}

			for (const projectResponse of watchedProjectResponses.value) {
				if (projectResponse.status !== "fulfilled") {
					console.error(
						"Error fetching project review requests:",
						projectResponse.reason,
					);
					throw new Error(
						"Could not retrieve some Project Merge Requests to review",
					);
				}

				const projectMRs = (
					projectResponse.value.data.project?.mergeRequests?.nodes || []
				).filter((mr) => mr != null);

				// Add MRs not already in reviewRequests
				for (const mr of projectMRs) {
					if (!reviewRequests.some((req) => req.id === mr.id)) {
						reviewRequests.push(mr);
					}
				}
			}

			await Promise.all([
				enrichMergeRequestsWithExpensiveFields(reviewRequests),
				fetchProjectMergeRequestFields(reviewRequests),
			]);

			const transformedPendingReviewMRs = reviewRequests
				.map(transformMergeRequest)
				.filter(excludeArchivedProjects)
				.filter(excludeMyPrs);

			let transformedMyOpenMRs = [];

			try {
				if (myOpenMRsResponse.status !== "fulfilled") {
					throw myOpenMRsResponse.reason;
				}

				const myOpenMRsNodes = (
					myOpenMRsResponse.value.data.currentUser.openRequests.nodes || []
				).filter((mr) => mr != null);

				await Promise.all([
					enrichMergeRequestsWithExpensiveFields(myOpenMRsNodes),
					fetchProjectMergeRequestFields(myOpenMRsNodes),
				]);

				transformedMyOpenMRs = myOpenMRsNodes
					.map(transformMergeRequest)
					.filter(excludeArchivedProjects);
			} catch (err) {
				const pendingReviewMRs = annotateStackedMergeRequests(
					transformedPendingReviewMRs,
				);
				applyProjectRules(pendingReviewMRs);
				setData((prevData) => ({
					...prevData,
					pendingReviewMRs,
					loaded: true,
				}));
				throw err;
			}

			const pendingIds = new Set(
				transformedPendingReviewMRs.map((mr) => mr.id),
			);
			const openMRs = [
				...transformedPendingReviewMRs,
				...transformedMyOpenMRs.filter((mr) => !pendingIds.has(mr.id)),
			];
			// External members only shape the stacks; they are not shown as cards.
			const publish = (externalMRs = []) => {
				const annotatedById = new Map(
					annotateStackedMergeRequests([...openMRs, ...externalMRs]).map(
						(mr) => [mr.id, mr],
					),
				);
				const pendingReviewMRs = transformedPendingReviewMRs.map(
					(mr) => annotatedById.get(mr.id) || mr,
				);
				const filteredMyOpenMRs = transformedMyOpenMRs.map(
					(mr) => annotatedById.get(mr.id) || mr,
				);

				applyProjectRules(pendingReviewMRs);
				applyProjectRules(filteredMyOpenMRs);

				setData((prevData) => ({
					...prevData,
					pendingReviewMRs,
					myOpenMRs: filteredMyOpenMRs,
					loaded: true,
				}));
			};

			// Show the cards straight away, stacked with the previous refresh's
			// dependencies and off-dashboard members; fresh ones fill in after.
			const { blockersById, externalMRs: lastExternalMRs } =
				lastStackData.current;
			for (const mr of openMRs) {
				Object.assign(mr, blockersById.get(mr.id));
			}
			const openIds = new Set(openMRs.map((mr) => mr.id));
			publish(lastExternalMRs.filter((mr) => !openIds.has(mr.id)));

			// Update hasIncompleteResults flag after all queries complete
			setData((prevData) => ({
				...prevData,
				hasIncompleteResults: hasAnyIncompleteResults,
			}));

			// Record when the primary data was last refreshed successfully
			setLastUpdated(new Date().toISOString());

			await fetchBlockingMergeRequests(openMRs);
			const externalMRs = await fetchExternalStackMembers(openMRs);
			if (fetchId === openFetchId.current) {
				lastStackData.current = {
					blockersById: new Map(
						openMRs.map(
							({ id, blockingMergeRequests, hiddenBlockingCount }) => [
								id,
								{ blockingMergeRequests, hiddenBlockingCount },
							],
						),
					),
					externalMRs,
				};
				publish(externalMRs);
			}
		} catch (err) {
			console.error("Error in fetchOpenData:", err);
			setError(err.message);
		} finally {
			setIsLoadingOpen(false);
		}
	}, [
		executeQuery,
		transformMergeRequest,
		user,
		setData,
		enrichMergeRequestsWithExpensiveFields,
		fetchProjectMergeRequestFields,
		fetchExternalStackMembers,
		fetchBlockingMergeRequests,
		applyProjectRules,
		watchProjects,
	]);

	const fetchClosedData = useCallback(async () => {
		if (!user?.access_token) return;

		setIsLoadingClosed(true);

		try {
			const closedDataResult = await executeQuery(CLOSED_DATA_QUERY);
			const closedData = closedDataResult.data.currentUser;

			const recentlyClosedMRs = [
				...new Map(
					[
						...(closedData.mergedRequests.nodes || []).filter(
							(mr) => mr != null,
						),
						...(closedData.closedRequests.nodes || []).filter(
							(mr) => mr != null,
						),
						...(closedData.mergedReviewRequests.nodes || []).filter(
							(mr) => mr != null,
						),
						...(closedData.closedReviewRequests.nodes || []).filter(
							(mr) => mr != null,
						),
					]
						.map(transformMergeRequest)
						.filter(excludeArchivedProjects)
						.map((mr) => [mr.iid, mr]),
				).values(),
			]
				.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
				.slice(0, 30);

			setData((prevData) => ({
				...prevData,
				recentlyClosedMRs,
			}));
		} catch (err) {
			console.error("Error fetching closed merge requests:", err);
		} finally {
			setIsLoadingClosed(false);
		}
	}, [executeQuery, transformMergeRequest, user, setData]);

	const fetchAllData = useCallback(async () => {
		setError("");
		await Promise.allSettled([fetchOpenData(), fetchClosedData()]);
	}, [fetchOpenData, fetchClosedData]);

	useEffect(() => {
		if (user?.access_token && !profileFetched) {
			fetchUserProfile();
		}
	}, [user, fetchUserProfile, profileFetched]);

	useEffect(() => {
		if (
			user?.access_token &&
			lastFailedRequest &&
			lastFailedRequest.accessToken !== user.access_token
		) {
			console.log("Retrying failed request with new token");
			const retryRequest = async () => {
				try {
					setError(null);
					if (
						lastFailedRequest.query === REVIEW_REQUESTS_QUERY ||
						lastFailedRequest.query === MY_OPEN_MRS_QUERY ||
						lastFailedRequest.query.includes("getWatchedProjectMRs") ||
						lastFailedRequest.query.includes("getProjectMrFields")
					) {
						await fetchOpenData();
					} else if (
						lastFailedRequest.query.includes("Closed") ||
						lastFailedRequest.query.includes("Merged")
					) {
						await fetchClosedData();
					}
				} catch (error) {
					console.error("Retry with new token failed:", error);
					setError(error.message);
				}
			};
			retryRequest();
		}
	}, [user, lastFailedRequest, fetchOpenData, fetchClosedData]);

	useEffect(() => {
		if (user?.access_token) {
			let running = true;
			const run = () => {
				if (running) {
					fetchAllData().finally(() => {
						setTimeout(run, REFRESH_INTERVAL);
					});
				}
			};
			run();
			return () => {
				running = false;
			};
		}
	}, [user, fetchAllData]);

	return {
		...data,
		loading: isLoadingOpen && !data.loaded,
		loadingClosed: isLoadingClosed && !data.loaded,
		error,
		lastUpdated,
		refetch: fetchAllData,
		isRefreshing: hasQueries,
	};
};
